import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { ArrowLeft, FileText, AlertCircle, Plus } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const NewRequest = () => {
  const { user } = useAuth();
  const { userTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [processSteps, setProcessSteps] = useState([]);
  const navigate = useNavigate();

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const cardStyles = userTheme?.cards || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, stepsRes] = await Promise.all([
        api.get('/user/stats').catch(() => ({ data: {} })),
        api.get('/quote-process/steps').catch(() => ({ data: [] }))
      ]);
      setUserStats(statsRes.data);
      setProcessSteps(stepsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuoteRequest = async () => {
    setCreating(true);
    try {
      const response = await api.post('/quote-process/requests');
      toast.success('Quote request created! Complete each step to proceed.');
      navigate(`/admin/requests/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote request');
    } finally {
      setCreating(false);
    }
  };

  // Check if user can submit based on new tier system
  const canSubmit = userStats?.can_submit_loi || 
    (userStats?.tier && ['silver', 'gold'].includes(userStats.tier) && userStats?.tier_status === 'approved');

  if (loading) {
    return (
      <Sidebar>
        <div 
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div 
            className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  // Show access denied if not approved
  if (!canSubmit) {
    const tierMessage = !userStats?.tier || userStats.tier === 'none' 
      ? "Complete Bronze verification to continue."
      : userStats.tier === 'bronze'
        ? "Silver verification is required to request quotes."
        : userStats.tier_status === 'pending'
          ? `Your ${userStats.tier} verification is pending admin approval.`
          : "Your account verification must be approved to request quotes.";

    return (
      <Sidebar>
        <div 
          className="p-8 min-h-screen"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <button
            onClick={() => navigate('/admin/dashboard')}
            data-testid="back-button"
            className="mb-6 inline-flex items-center gap-2 text-sm hover:opacity-80 transition"
            style={{ color: primaryColor }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          <div 
            className="rounded-xl shadow-sm p-8 text-center max-w-2xl"
            style={{ 
              backgroundColor: cardStyles.backgroundColor || '#FFFFFF',
              border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}`
            }}
          >
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 
              className="text-2xl font-semibold mb-2"
              style={{ color: cardStyles.headerColor || '#111827' }}
            >
              Verification Required
            </h2>
            <p className="mb-6" style={{ color: cardStyles.textColor || '#6B7280' }}>{tierMessage}</p>
            <div className="flex flex-col items-center gap-3">
              <div className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full font-medium">
                Current Tier: {userStats?.tier?.charAt(0).toUpperCase() + userStats?.tier?.slice(1) || 'None'} 
                ({userStats?.tier_status || 'Not Started'})
              </div>
              {userStats?.tier !== 'silver' && userStats?.tier !== 'gold' && (
                <button
                  onClick={() => navigate(userStats?.tier === 'bronze' ? '/admin/verify/silver' : '/admin/verify/bronze')}
                  className="px-6 py-2 text-white rounded-lg transition hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  Complete Verification
                </button>
              )}
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        <button
          onClick={() => navigate('/admin/dashboard')}
          data-testid="back-button"
          className="mb-6 inline-flex items-center gap-2 text-sm hover:opacity-80 transition"
          style={{ color: primaryColor }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div 
          className="rounded-xl shadow-sm p-8 max-w-3xl"
          style={{ 
            backgroundColor: cardStyles.backgroundColor || '#FFFFFF',
            border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}`
          }}
        >
          <div className="mb-8">
            <h1 
              className="text-2xl font-semibold mb-2"
              style={{ color: cardStyles.headerColor || '#111827' }}
            >
              Request a Quote
            </h1>
            <p style={{ color: cardStyles.textColor || '#6B7280' }}>
              Start a new quote request to import commodities. You'll complete each step of the process sequentially.
            </p>
          </div>

          {/* Process Steps Preview */}
          <div className="mb-8">
            <h2 
              className="text-lg font-semibold mb-4"
              style={{ color: cardStyles.headerColor || '#111827' }}
            >
              The Process ({processSteps.length} steps)
            </h2>
            <div className="space-y-2">
              {processSteps.map((step, index) => (
                <div 
                  key={step.id} 
                  className="flex items-center gap-3 p-3 rounded-lg"
                  style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: cardStyles.headerColor || '#111827' }}>
                      {step.name}
                    </p>
                    {step.description && (
                      <p className="text-xs" style={{ color: cardStyles.textColor || '#6B7280' }}>
                        {step.description}
                      </p>
                    )}
                  </div>
                  {!step.is_required && (
                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">Optional</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {processSteps.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: cardStyles.borderColor || '#D1D5DB' }} />
              <p style={{ color: cardStyles.textColor || '#6B7280' }}>No process steps configured. Please contact support.</p>
            </div>
          ) : (
            <div className="pt-6" style={{ borderTop: `1px solid ${cardStyles.borderColor || '#E5E7EB'}` }}>
              <button
                onClick={handleCreateQuoteRequest}
                data-testid="start-quote-button"
                disabled={creating}
                className="w-full flex items-center justify-center gap-2 text-white py-4 px-8 rounded-xl font-medium transition-all disabled:opacity-50 hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Start Quote Request
                  </>
                )}
              </button>
              <p className="text-center text-xs mt-3" style={{ color: cardStyles.textColor || '#6B7280' }}>
                You can save your progress and continue later
              </p>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default NewRequest;
